import React, { useState, type SyntheticEvent } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/standard-button';
import DropdownMenu, {
	DropdownItemCheckbox,
	DropdownItemCheckboxGroup,
	type OnOpenChangeArgs,
} from '@atlaskit/dropdown-menu';
import ChevronDownIcon from '@atlaskit/icon/glyph/chevron-down';
import FilterIcon from '@atlaskit/icon/glyph/filter';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { useHasCurrentUserOpsgenieAccount } from '@atlassian/jira-servicedesk-incident-management-common/src/services/has-opsgenie-account';
import { jiraOnlyFilters, jsmBaseFilters, jsmIncidentFilters } from '../../common/constants';
import type { FilterDropdownOption, IncidentActivityFeedEntryId } from '../../common/types';
import { useIsJiraServiceManagementRequest } from '../../common/utils';
import { useActivityFeedSelectedFilters } from '../../services';
import messages from './messages';

export type FilterButtonProps = {
	isIssueOfIncidentsPractice?: boolean;
	isIncidentTimelineModalOpen?: boolean;
};

type ToggleFilterArg = {
	clickedFilterId: IncidentActivityFeedEntryId;
	filters: readonly IncidentActivityFeedEntryId[];
};

export const toggleFilters = ({
	clickedFilterId,
	filters,
}: ToggleFilterArg): readonly IncidentActivityFeedEntryId[] => {
	let updatedFilters: readonly IncidentActivityFeedEntryId[] = filters;
	const isFilterChecked = updatedFilters.indexOf(clickedFilterId) === -1; // was not checked before, user checked it

	if (isFilterChecked) {
		updatedFilters = [...updatedFilters, clickedFilterId];
	} else {
		updatedFilters = updatedFilters.filter((filterId) => filterId !== clickedFilterId);
	}

	return updatedFilters;
};

const getDropdownOptions = (
	hasOpsgenieAccount: boolean,
	isIssueOfIncidentsPractice: boolean,
	shouldShowJsmFilters: boolean,
	isJiraServiceManagementRequest: boolean,
) => {
	switch (true) {
		case hasOpsgenieAccount === true && (isIssueOfIncidentsPractice || shouldShowJsmFilters):
			return jsmIncidentFilters;
		case isJiraServiceManagementRequest:
			return jsmBaseFilters;
		default:
			return jiraOnlyFilters;
	}
};

export const ActivityFeedFilterButton = ({
	isIssueOfIncidentsPractice = false,
	isIncidentTimelineModalOpen = false,
}: FilterButtonProps) => {
	const { formatMessage } = useIntl();
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [selectedFilters, { setSelectedFilters }] = useActivityFeedSelectedFilters(
		isIncidentTimelineModalOpen,
	);

	const filterActivityFeed = (id: IncidentActivityFeedEntryId) => {
		const updatedGroupFilters = toggleFilters({
			clickedFilterId: id,
			filters: selectedFilters,
		});

		setSelectedFilters(updatedGroupFilters);
	};

	const isInSelectedFilters = (id: IncidentActivityFeedEntryId) =>
		selectedFilters.indexOf(id) !== -1;
	const [hasOpsgenieAccount] = useHasCurrentUserOpsgenieAccount();

	const isJSM = useIsJiraServiceManagementRequest();

	const shouldShowJsmFilters = isIncidentTimelineModalOpen;

	const dropdownOptions = getDropdownOptions(
		!!hasOpsgenieAccount,
		isIssueOfIncidentsPractice,
		shouldShowJsmFilters,
		isJSM,
	);
	const checkboxAnalyticsEvent = createAnalyticsEvent({
		action: 'clicked',
		actionSubject: 'checkbox',
	});
	return (
		<DropdownMenu
			placement="bottom-start"
			isOpen={isDropdownOpen}
			onOpenChange={(attrs: OnOpenChangeArgs) => {
				setIsDropdownOpen(attrs.isOpen);
			}}
			testId="jsm-issue-activity-filter.ui.activity-feed-filter-button.dropdown-menu"
			trigger={({ triggerRef, ...btnProps }) => (
				<Button
					{...btnProps}
					ref={triggerRef}
					appearance="default"
					isSelected={isDropdownOpen}
					iconBefore={<FilterIcon size="medium" label="" />}
					iconAfter={<ChevronDownIcon size="medium" label="" />}
					onClick={(event: SyntheticEvent, analyticsEvent: UIAnalyticsEvent) => {
						setIsDropdownOpen(!isDropdownOpen);
						fireUIAnalytics(analyticsEvent, 'activityFeedFilter', {
							isJsmIncident: isIssueOfIncidentsPractice,
						});
					}}
					interactionName="auto-generated.jira-jsm-issue-activity-filter.ui.activity-feed-filter-button.button"
				>
					{formatMessage(messages.filterButtonLabel)}
				</Button>
			)}
		>
			<DropdownItemCheckboxGroup id="activityFeedFilterDropdown">
				{dropdownOptions.map((option: FilterDropdownOption) => (
					<DropdownItemCheckbox
						key={option.id}
						id={option.id}
						isSelected={isInSelectedFilters(option.id)}
						onClick={() => {
							filterActivityFeed(option.id);
							fireUIAnalytics(checkboxAnalyticsEvent, 'activityFeedFilter', {
								isJsmIncident: isIssueOfIncidentsPractice,
								filterId: option.id,
								isChecked: !isInSelectedFilters(option.id),
							});
						}}
					>
						{formatMessage(option.i18nMessage)}
					</DropdownItemCheckbox>
				))}
			</DropdownItemCheckboxGroup>
		</DropdownMenu>
	);
};
